import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import '../styles/auth.css';

const PW_RULES = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'One uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: p => /[a-z]/.test(p) },
  { label: 'One number', test: p => /[0-9]/.test(p) },
  { label: 'One special character', test: p => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(p) },
];

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [step, setStep] = useState('validating');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setStep('invalid'); return; }
    fetch('/api/auth/validate-reset-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    }).then(r => r.json()).then(data => {
      if (data.error) { setError(data.error); setStep('invalid'); }
      else { setEmail(data.email); setStep('reset'); }
    }).catch(() => { setError('Connection error'); setStep('invalid'); });
  }, [token]);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) { setError('Passwords do not match'); return; }
    const failing = PW_RULES.filter(r => !r.test(password));
    if (failing.length) { setError('Password does not meet all requirements'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || data.details?.join(', ')); setLoading(false); return; }
      setDone(true);
      setLoading(false);
    } catch { setError('Connection error'); setLoading(false); }
  };

  if (step === 'validating') {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <p className="auth-subtitle" style={{ textAlign: 'center' }}>Validating reset link...</p>
      </div></div>
    );
  }

  if (step === 'invalid') {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <div className="auth-error">{error || 'Invalid or expired reset link.'}</div>
        <Link to="/forgot-password"><button className="auth-btn secondary">Request New Reset Link</button></Link>
      </div></div>
    );
  }

  if (done) {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <div className="auth-success">Your password has been reset successfully!</div>
        <Link to="/login"><button className="auth-btn">Sign In</button></Link>
      </div></div>
    );
  }

  return (
    <div className="auth-page"><div className="auth-card">
      <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
      <h2>Create New Password</h2>
      <p className="auth-subtitle">Set a new password for <strong style={{ color: '#e2e8f0' }}>{email}</strong></p>
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleReset}>
        <div className="auth-field">
          <label>New Password</label>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a strong password" required autoFocus />
          <ul className="auth-pw-reqs">
            {PW_RULES.map((r, i) => (
              <li key={i} className={r.test(password) ? 'met' : 'unmet'}>
                {r.test(password) ? '✓' : '○'} {r.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="auth-field">
          <label>Confirm New Password</label>
          <PasswordInput value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm your password" required />
        </div>
        <button className="auth-btn" type="submit" disabled={loading}>{loading ? 'Resetting...' : 'Reset Password'}</button>
      </form>
    </div></div>
  );
}
